/**
 * ``useSse`` — connection-state machine over the BFF's
 * ``GET /api/query/sse`` endpoint via native :class:`EventSource`.
 *
 * Stream A PR 2 / commit M3 — owns the Flow A state machine
 * (`docs/engineering/detail/storyboards/flows/A-sse-recovery/README.md`)
 * + delivers ``status`` / ``detection`` / ``alert`` / ``alert-dismissed``
 * messages to consumers via the :func:`subscribe` surface. Companion to
 * :func:`useSnapshot`: when
 * the SSE stream reconnects, this hook invalidates the snapshot
 * query so a fresh snapshot is fetched before live updates resume
 * (REQ-UI-009).
 *
 * # Message delivery is a subscription, not a value (issue #739)
 *
 * This hook previously exposed the stream as ``lastMessage``, a single
 * most-recent-message slot that consumers watched with an effect. That
 * dropped every message but the last whenever several arrived between two
 * renders — the normal case when one ``text/event-stream`` flush carries
 * several frames. Consumers now register a callback via :func:`subscribe`
 * and are invoked synchronously per message, so delivery never passes
 * through React's batching. :file:`map/sse-subscription.ts` carries the
 * full rationale and the ordering guarantees.
 *
 * # State machine (per Flow A storyboard)
 *
 * Six states; the visual treatment lives on the map-stream row of
 * :func:`StatusStreamSections` in :file:`entity-details/status-stream-sections.tsx`
 * (relocated from the top bar by REQ-STATUS-048 / -049).
 *
 * | State           | Trigger                                  | Transition timing |
 * |-----------------|------------------------------------------|---|
 * | ``idle``        | No viewport published yet (Map closed)    | — (see "Idle is not reconnecting" below) |
 * | ``live``        | EventSource open + receiving messages    | — |
 * | ``reconnecting``| EventSource ``onerror`` (drop / close)   | ~800 ms after drop (flap-avoidance per Flow A "Very brief disconnect (< 1 s)") |
 * | ``disconnected``| Still reconnecting at 30 s elapsed       | 30 s without recovery |
 * | ``fail-stop``   | Still reconnecting at 5 min elapsed      | 5 min without recovery |
 * | ``paused``      | Operator-driven pause (Stream B trigger) | — (M3 reserves the state for Stream B) |
 *
 * # Idle is not reconnecting (REQ-STATUS-049 blocking prerequisite)
 *
 * The hook used to report ``reconnecting`` whenever ``viewport === null``.
 * Only :class:`Map` ever publishes a viewport, and the Map is one dockable
 * panel among many (ADR-024) — so in every workspace where the Map panel is
 * closed, undocked or never opened, the map stream reported a permanent
 * amber "reconnecting" while nothing was wrong and nothing was being
 * retried. REQ-STATUS-049 surfaces this state in the User Interface Manager
 * Selection Detail and REQ-STATUS-048's fail-safe precedence reasons about
 * client-observed liveness, so the state had to become truthful before
 * either could consume it: ``idle`` says "deliberately not subscribed",
 * ``reconnecting`` says "was subscribed, lost it, retrying".
 *
 * Native :class:`EventSource` auto-reconnects on connection close —
 * the hook does NOT implement retry-with-backoff logic itself. The
 * browser fires ``onerror`` on each drop, ``onopen`` on each
 * successful reconnect; the hook reflects state via these events +
 * the timer ladder (:file:`@/map/sse-disconnect-ladder`, shared with the
 * status stream). The visible "Attempt N of ∞" treatment from
 * Flow A Frame 3 is M8's concern; M3 owns the state-machine logic.
 *
 * Flap-avoidance: the storyboard explicitly says "Very brief
 * disconnect (< 1 s). UI does not transition to RECONNECTING — the
 * reconnect happens before the state-transition timer fires." The
 * hook schedules the ``live → reconnecting`` transition with an
 * ~800 ms delay so a fast reconnect doesn't flash the indicator
 * amber. The visible amber arrives well within the
 * "within 1.5 seconds" upper bound from Flow A Frame 2.
 *
 * # Snapshot refresh on reconnect (REQ-UI-009)
 *
 * Per Flow A Frame 4: "The new BFF instance does **not** assume
 * continuity from the prior instance — it serves a **fresh
 * snapshot** plus an SSE stream of subsequent updates."
 *
 * On every successful ``EventSource.onopen`` after a prior drop, the
 * hook invalidates the TanStack Query snapshot cache via
 * :func:`queryClient.invalidateQueries`. The :class:`Map` component's
 * :func:`useSnapshot` re-fetches; the snapshot-layer applies the
 * fresh payload; live SSE deltas resume merging on top.
 *
 * # Authentication
 *
 * Native :class:`EventSource` includes credentials when
 * ``withCredentials: true`` is set on the constructor; the BFF's
 * SSE route (``packages/meridian_bff/src/meridian_bff/_query_sse.py``
 * §build_sse_router) requires the session cookie + the
 * ``map_reader`` role bundle. No CSRF header — :class:`EventSource`
 * cannot set custom headers, and the BFF's SSE route is a GET that
 * does not validate CSRF (consistent with REQ-UI-035 + ADR-009).
 *
 * # Lifecycle + cleanup
 *
 * On mount: open the :class:`EventSource`. On unmount: close it.
 * All timers cleared in cleanup so a re-mount doesn't double-fire
 * the state-transition ladder.
 *
 * # Why state machine in a hook rather than a reducer
 *
 * The state surface is small (6 states + one timer ladder); a hook with
 * :func:`useRef` for the imperative :class:`EventSource` instance +
 * :func:`useState` for the React-tracked state is the smallest
 * surface that reads cleanly + tests cleanly with fake timers. A
 * reducer would be marginally more declarative but adds indirection
 * the test suite would have to navigate.
 *
 * Cross-references:
 *   - REQ-UI-006 (live update marker patch ≤ 100 ms).
 *   - REQ-UI-007 (viewport → BFF — partial: M3 opens SSE with the
 *     active viewport; pan-driven re-subscribe is M4's concern).
 *   - REQ-UI-008 (automatic reconnect — native EventSource).
 *   - REQ-UI-009 (fresh snapshot before SSE resume — invalidate-
 *     queries on reconnect).
 *   - ADR-016 §1 (snapshot + SSE pattern) + §3 (reconnect-via-
 *     snapshot).
 *   - docs/engineering/detail/storyboards/flows/A-sse-recovery/README.md.
 *   - packages/meridian_bff/src/meridian_bff/_query_sse.py.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { parseSseMessage, type SseMessage } from "@/map/sse-types";
import { createDisconnectLadder } from "@/map/sse-disconnect-ladder";
import {
  createWorkerLiveSource,
  workerLiveSourceAvailable,
  type LiveBatchSource,
} from "@/live/worker-live-source";
import type { SseMessageHandler, SseSubscribe } from "@/map/sse-subscription";

/**
 * The states the hook surfaces, matching the Flow A storyboard's state
 * machine (§"Connection-state machine") plus ``idle``.
 *
 * - ``idle`` — No viewport has been published, so no connection is open. The
 *   Map panel is closed / undocked / not yet ready. **Not a failure and not a
 *   retry**: nothing is being attempted, so nothing can be recovering
 *   (REQ-STATUS-049 Notes — the state the map stream used to lie about).
 * - ``live`` — EventSource open + receiving (or recently received) messages.
 * - ``reconnecting`` — Stream dropped; browser is auto-reconnecting; < 30 s elapsed.
 * - ``disconnected`` — Still reconnecting; ≥ 30 s elapsed.
 * - ``fail-stop`` — Still reconnecting; ≥ 5 min elapsed.
 * - ``paused`` — Operator paused (Stream B trigger; M3 reserves the state).
 *
 * Cross-references:
 *   - docs/engineering/detail/storyboards/flows/A-sse-recovery/README.md
 *     §"Connection-state machine".
 *   - REQ-STATUS-049 (client-observed stream states on User Interface Manager).
 */
export type SseConnectionState =
  "idle" | "live" | "reconnecting" | "disconnected" | "fail-stop" | "paused";

/**
 * Threshold timings for the Flow A ladder. Defined in
 * :file:`@/map/sse-disconnect-ladder` (the ladder itself lives there since the
 * status stream started sharing it) and re-exported here so every existing
 * ``import { SSE_TIMINGS } from "@/map/use-sse"`` keeps resolving.
 */
export { SSE_TIMINGS } from "@/map/sse-disconnect-ladder";

/**
 * The hook's public surface. Provider-context consumers
 * destructure these into the :class:`ConnectionStatusIndicator`
 * (state, lastEventTime) + the SSE message consumers, which register
 * against :func:`subscribe`.
 */
export interface SseConnection {
  /** Current Flow A state. */
  state: SseConnectionState;
  /**
   * Wall-clock timestamp (ms since epoch) of the most recently
   * received message, or ``null`` when no message has yet arrived.
   * The :class:`ConnectionStatusIndicator` reads this to drive the
   * LIVE-IDLE pulse (off when ``Date.now() - lastEventTime > 30 s``
   * per Flow A §"Connection-state machine"). This is derived
   * rendering, not state.
   *
   * ⚠ This is a *display* signal and is deliberately coalescing — it
   * answers "how long since anything arrived", so a burst collapsing
   * to one timestamp is correct. Never read it as a message counter.
   */
  lastEventTime: number | null;
  /**
   * Frames the BFF dropped for THIS connection because the browser fell
   * behind (the ``meridian-dropped`` announcement; REQ-UI-197). Cumulative
   * for the life of one connection, reset to 0 on every (re)open — the
   * server's counter restarts with the connection. The stream rows show it
   * as "feed behind" so a server-side drop is visible to the operator, not
   * only on the BFF node's telemetry.
   */
  droppedFrames: number;
  /**
   * Register a handler that receives EVERY message on the stream, in
   * arrival order. Returns the unsubscribe function.
   *
   * Stable across renders, so it is safe as an effect dependency.
   * Prefer :func:`sse-subscription.useSseMessages` over calling this
   * directly — it handles the ref-stable-handler plumbing.
   *
   * # Why a subscription and not a ``lastMessage`` value (issue #739)
   *
   * A single most-recent-message slot silently drops every message but
   * the last whenever several arrive between two renders — which is the
   * normal case when one ``text/event-stream`` flush carries several
   * frames. Handlers here are invoked synchronously as each message is
   * parsed, so delivery never passes through React's batching at all.
   * See :file:`map/sse-subscription.ts` for the full rationale.
   */
  subscribe: SseSubscribe;
}

/**
 * Options accepted by :func:`useSse`.
 *
 * The viewport is optional — when ``null``, no SSE connection is
 * opened (the hook is gated on the map being loaded). When non-
 * null, the BFF receives the bbox params and applies the same
 * viewport filter the snapshot endpoint did.
 */
export interface UseSseOptions {
  /** Active viewport bounds. ``null`` keeps the SSE connection
   *  closed (M3 ungates on the map's ``onMapReady`` callback). */
  viewport: SseViewport | null;
  /** Override the default endpoint path. Defaults to
   *  ``/api/query/sse``. Tests can point at a fake endpoint via
   *  ``server.use(...)`` + MSW SSE handler. */
  endpoint?: string;
  /**
   * Custom :class:`EventSource` factory. Tests inject a fake to
   *  drive the state machine via :func:`dispatchEvent` without
   *  going through MSW. Production code leaves this undefined; the
   *  hook calls ``new EventSource(url, { withCredentials: true })``.
   */
  eventSourceFactory?: (url: string) => EventSource;
  /**
   * Custom worker-backed source factory (feature spec ``ui-track-load-headroom``
   * §2.5; REQ-UI-193). Production leaves it undefined and gets the live-data
   * worker when the environment has ``Worker``; a factory that throws
   * falls the hook back to a main-thread ``EventSource``. Tests inject a
   * fake :type:`LiveBatchSource`. Ignored when ``eventSourceFactory`` is
   * given (that path is the direct one).
   */
  liveSourceFactory?: (url: string) => LiveBatchSource;
}

/**
 * Viewport bounds for the SSE subscribe. Matches
 * :type:`SnapshotViewport` shape but kept separate so the snapshot
 * hook's bbox semantics don't accidentally bleed into the SSE
 * hook's — both endpoints take the same four floats but the
 * lifecycle differs.
 */
export interface SseViewport {
  min_longitude: number;
  min_latitude: number;
  max_longitude: number;
  max_latitude: number;
}

/**
 * Compose the SSE endpoint URL with viewport query params.
 *
 * Exported for unit-test reach into the URL-composition without
 * spinning up the full hook.
 */
export function composeSseUrl(endpoint: string, viewport: SseViewport): string {
  const params = new URLSearchParams({
    min_longitude: viewport.min_longitude.toString(),
    min_latitude: viewport.min_latitude.toString(),
    max_longitude: viewport.max_longitude.toString(),
    max_latitude: viewport.max_latitude.toString(),
  });
  return `${endpoint}?${params.toString()}`;
}

/**
 * The TanStack Query prefix for the snapshot cache. Re-stated here
 * to avoid the SSE module importing the snapshot module (which
 * pulls in TanStack Query + the snapshot-types). The hook
 * invalidates queries whose key starts with this prefix on every
 * successful reconnect, matching :func:`snapshotQueryKey`'s shape
 * (see :file:`use-snapshot.ts`).
 *
 * Keep synchronized with :func:`snapshotQueryKey` in
 * :file:`use-snapshot.ts`.
 *
 * Cross-references:
 *   - REQ-UI-009.
 */
export const SNAPSHOT_QUERY_KEY_PREFIX: readonly unknown[] = ["snapshot"];

/**
 * Open + manage the SSE connection. Returns the current connection
 * state, last-event-time, and last-message.
 *
 * Mounting / unmounting: the hook owns the :class:`EventSource`
 * lifecycle. On viewport change, the existing connection is closed
 * and a fresh one is opened — the BFF applies the new viewport
 * filter. This is straightforward bbox-change re-subscribe semantics
 * (REQ-UI-007 partial; M4 finalises pan-debounce + on-change-only).
 *
 * @param options - Viewport, endpoint override (tests), EventSource
 *     factory override (tests).
 * @returns The :type:`SseConnection` snapshot.
 *
 * Cross-references:
 *   - REQ-UI-006 / REQ-UI-007 / REQ-UI-008 / REQ-UI-009.
 *   - ADR-016 §1 + §3.
 *   - docs/engineering/detail/storyboards/flows/A-sse-recovery/README.md.
 */
export function useSse(options: UseSseOptions): SseConnection {
  const queryClient = useQueryClient();
  const { viewport, endpoint = "/api/query/sse", eventSourceFactory, liveSourceFactory } = options;

  const [state, setState] = useState<SseConnectionState>(() =>
    viewport === null ? "idle" : "live",
  );
  const [lastEventTime, setLastEventTime] = useState<number | null>(null);
  const [droppedFrames, setDroppedFrames] = useState(0);

  // The live subscriber set (issue #739). Held in a ref, NOT in state, and
  // deliberately outside the EventSource effect below: subscriptions belong
  // to consumers and must survive a connection teardown/reopen (which a
  // viewport change triggers). Messages are fanned out to this set
  // synchronously, so no message delivery passes through React batching —
  // that is what stops a multi-frame flush from coalescing.
  const subscribersRef = useRef<Set<SseMessageHandler>>(new Set());

  /**
   * Stable :type:`SseSubscribe` — empty dep list, so consumers can use it
   * as an effect dependency without re-subscribing on every render.
   */
  const subscribe = useCallback<SseSubscribe>((handler) => {
    subscribersRef.current.add(handler);
    return () => {
      subscribersRef.current.delete(handler);
    };
  }, []);

  // Ref for the imperative :class:`EventSource`, held so the effect's
  // cleanup can reach it without re-running on state change. The Flow A
  // timers live in a per-effect ladder (below), not in refs.
  const sourceRef = useRef<EventSource | null>(null);

  // Track whether the *previous* state was a connected one so we
  // can fire the snapshot-invalidation only on actual recoveries
  // (not on initial open). ``true`` after the first ``onopen``.
  const everConnectedRef = useRef<boolean>(false);
  // Track whether we are currently in a non-live state — the
  // snapshot-invalidation fires only when ``everConnectedRef`` is
  // true AND we are transitioning *out of* a disconnect.
  const isDisconnectedRef = useRef<boolean>(false);

  useEffect(() => {
    // No viewport yet — keep the connection closed and say so. ``idle``,
    // not ``reconnecting``: the Map panel may simply be closed (ADR-024
    // makes it one dockable panel among many), in which case there is no
    // subscription to recover and no retry in flight. Reporting amber here
    // was a standing falsehood on an always-visible surface, and
    // REQ-STATUS-048 / -049 now read this state — see the module docstring
    // §"Idle is not reconnecting". ``idle`` is not green either: it never
    // claims data is flowing (CLAUDE.md §8.1).
    if (viewport === null) {
      setState("idle");
      return undefined;
    }

    // The Flow A ladder (flap-avoidance → reconnecting → disconnected →
    // fail-stop), shared with the status stream; see
    // :file:`@/map/sse-disconnect-ladder`. Display-side only — the browser's
    // native EventSource retry is what actually reconnects here.
    const ladder = createDisconnectLadder({
      onReconnecting: () => setState("reconnecting"),
      onDisconnected: () => setState("disconnected"),
      onFailStop: () => setState("fail-stop"),
    });

    const url = composeSseUrl(endpoint, viewport);
    // The worker-backed source (REQ-UI-193) is the production path: the
    // live-data worker owns the EventSource, parses every frame off the
    // main thread and delivers one ordered batch per tick. A direct
    // ``eventSourceFactory`` (tests) takes the main-thread path; so does a
    // worker that cannot be built — the pre-worker behaviour, never no
    // live data (CLAUDE.md §8.1).
    let batchSource: LiveBatchSource | null = null;
    if (eventSourceFactory === undefined) {
      const factory =
        liveSourceFactory ?? (workerLiveSourceAvailable() ? createWorkerLiveSource : null);
      if (factory !== null) {
        try {
          batchSource = factory(url);
        } catch (error) {
          console.warn(
            "[meridian/map/use-sse] live-data worker unavailable; using a main-thread EventSource",
            error,
          );
        }
      }
    }

    // On open: clear the flap timer (so a quick reconnect doesn't
    // flash amber), promote state to ``live``, and if this is a
    // recovery (not the initial connect) invalidate the snapshot
    // cache per REQ-UI-009.
    const onOpen = (): void => {
      ladder.clear();
      setState("live");
      // A new connection: the server's per-client drop counter restarted.
      setDroppedFrames(0);

      if (everConnectedRef.current && isDisconnectedRef.current) {
        // Recovery from a prior drop — fresh snapshot per Flow A
        // Frame 4 + REQ-UI-009. The Map's useSnapshot hook re-
        // fetches; the snapshot-layer applies the fresh data.
        void queryClient.invalidateQueries({
          queryKey: SNAPSHOT_QUERY_KEY_PREFIX,
        });
      }
      everConnectedRef.current = true;
      isDisconnectedRef.current = false;
    };

    // Generic message handler — applies to the un-named "message"
    // event (which the BFF does not emit), plus the named handlers
    // below dispatch to the same setter.
    /**
     * Deliver one parsed message: the display signals, then the synchronous
     * fan-out. Shared by the direct path (one call per EventSource event)
     * and the worker path (one call per message of a batch).
     */
    const deliver = (parsed: SseMessage): void => {
      // ``lastEventTime`` is React state (it drives the indicator's LIVE-IDLE
      // pulse) and may coalesce across a burst — correct, since it answers
      // "how long since anything arrived".
      setLastEventTime(Date.now());
      if (parsed.kind === "meridian-dropped") {
        // The slow-consumer announcement carries the cumulative count
        // (REQ-UI-197); it is React state because the stream row displays
        // it. It still fans out below so a consumer can react to it.
        setDroppedFrames(parsed.payload.dropped);
      }
      // Message delivery, by contrast, must NOT coalesce (issue #739). Fan
      // out synchronously, in arrival order, so each frame in a multi-frame
      // flush reaches every consumer. Iterate a copy so a handler that
      // unsubscribes (or subscribes) during dispatch cannot corrupt the walk.
      for (const handler of [...subscribersRef.current]) {
        try {
          handler(parsed);
        } catch (error) {
          // Isolate, don't swallow: one throwing consumer must not stop the
          // others from seeing this message — a broken bridge blinding the
          // operator to alerts is the fail-open outcome CLAUDE.md §8.1
          // forbids. Logged with the message kind so the failure is
          // diagnosable from the browser console (§5.4 remote
          // diagnosability); the throw is not re-raised because the
          // remaining handlers still have to run.
          console.error(
            `[meridian/map/use-sse] SSE subscriber threw while handling a "${parsed.kind}" message; ` +
              `other subscribers still received it`,
            error,
          );
        }
      }
    };
    const warnMalformed = (eventName: string): void => {
      // Malformed payload or unknown event type — drop silently per
      // CLAUDE.md §8.1 (operator-visibility: a single bad payload doesn't
      // poison the stream). Surfaces in the browser console at warn level
      // so on-call diagnosis from the dev-tools is possible without
      // server-side logs.
      console.warn(
        `[meridian/map/use-sse] dropped malformed/unknown SSE payload (event=${eventName})`,
      );
    };
    // The direct (main-thread EventSource) path: parse here, then deliver.
    const handleMessage = (eventName: string, raw: string): void => {
      const parsed = parseSseMessage(eventName, raw);
      if (parsed === null) {
        warnMalformed(eventName);
        return;
      }
      deliver(parsed);
    };
    // ``onerror`` fires when the connection drops. Native
    // EventSource auto-reconnects; the hook's job is to surface the
    // disconnect state with flap-avoidance + ladder the escalations.
    // The browser re-fires ``onerror`` on every failed native retry;
    // only the first drop of an outage opens the ladder (``drop()`` is
    // ``false`` for the rest), so the escalation clock runs from the
    // moment the transport went away.
    const onError = (): void => {
      if (!ladder.drop()) {
        return;
      }
      isDisconnectedRef.current = true;
    };

    if (batchSource !== null) {
      const worker = batchSource;
      worker.addEventListener("open", onOpen);
      worker.addEventListener("error", onError);
      worker.onBatch((messages) => {
        for (const message of messages) {
          deliver(message);
        }
      });
      worker.onMalformed(warnMalformed);
      return () => {
        worker.removeEventListener("open", onOpen);
        worker.removeEventListener("error", onError);
        worker.close();
        ladder.clear();
      };
    }
    // The direct path. Construct the EventSource (or its test fake). The
    // ``withCredentials: true`` causes the browser to send the session
    // cookie — the BFF's SSE route requires it.
    const source: EventSource = eventSourceFactory
      ? eventSourceFactory(url)
      : new EventSource(url, { withCredentials: true });
    sourceRef.current = source;
    // Named-event listeners. The BFF emits ``event: status`` /
    // ``event: detection`` / ``event: alert`` / ``event: alert-dismissed`` /
    // ``event: meridian-dropped`` per :file:`_query_sse.py` §_format_sse_*.
    const onStatusMessage = (ev: MessageEvent<string>): void => {
      handleMessage("status", ev.data);
    };
    const onDetectionMessage = (ev: MessageEvent<string>): void => {
      handleMessage("detection", ev.data);
    };
    const onAlertMessage = (ev: MessageEvent<string>): void => {
      handleMessage("alert", ev.data);
    };
    const onAlertDismissedMessage = (ev: MessageEvent<string>): void => {
      handleMessage("alert-dismissed", ev.data);
    };
    const onDroppedMessage = (ev: MessageEvent<string>): void => {
      handleMessage("meridian-dropped", ev.data);
    };
    source.addEventListener("open", onOpen);
    source.addEventListener("status", onStatusMessage as EventListener);
    source.addEventListener("detection", onDetectionMessage as EventListener);
    source.addEventListener("alert", onAlertMessage as EventListener);
    source.addEventListener("alert-dismissed", onAlertDismissedMessage as EventListener);
    source.addEventListener("meridian-dropped", onDroppedMessage as EventListener);
    source.addEventListener("error", onError);
    return () => {
      // Unmount cleanup — close the connection + clear timers. The
      // browser would close the connection on page navigation
      // anyway, but explicit cleanup keeps StrictMode's double-mount
      // path well-behaved (no orphaned EventSources).
      source.removeEventListener("open", onOpen);
      source.removeEventListener("status", onStatusMessage as EventListener);
      source.removeEventListener("detection", onDetectionMessage as EventListener);
      source.removeEventListener("alert", onAlertMessage as EventListener);
      source.removeEventListener("alert-dismissed", onAlertDismissedMessage as EventListener);
      source.removeEventListener("meridian-dropped", onDroppedMessage as EventListener);
      source.removeEventListener("error", onError);
      source.close();
      sourceRef.current = null;
      ladder.clear();
    };
  }, [viewport, endpoint, eventSourceFactory, liveSourceFactory, queryClient]);

  // Memoised so the context value below only changes when something a
  // consumer can actually observe changes. ``subscribe`` is stable, so this
  // re-creates on state / lastEventTime transitions only.
  return useMemo<SseConnection>(
    () => ({ state, lastEventTime, droppedFrames, subscribe }),
    [state, lastEventTime, droppedFrames, subscribe],
  );
}
